/**
 * The card editor: pick a frame on the card, put a picture in it and place it,
 * write a note on it, and see both where the paper will put them.
 * Every change saves itself - on this device (local/store.ts); a save that
 * would overwrite a newer one from another window is refused and reported
 * here, never forced. The PDF is made here too (card-pdf.tsx), and the whole
 * card can be saved to a file to take to another device (local/card-file.ts).
 *
 * Redone 2026-10-17, as the owner asked ("I need more emphasis around clicking on the zone"):
 *  - a click on a frame opens its pop-up: the pictures to choose from (this card's, with
 *    "Add pictures", and the picture library), then everything about that frame - its
 *    picture's look and position, its shape (and, for the two halves of Reveal 1, moving
 *    the shape up or down - with the buttons, or by dragging it on the card), its message;
 *  - ONE "Fill my card" button, above the card, for what most people will do first: it
 *    fills the empty frames (faces/place.ts), and once every frame is full it makes
 *    another arrangement; Undo puts the card back;
 *  - zooming in on a frame from the card's corner, or with a double click on the frame;
 *  - the tool panel folds away to the side with an arrow.
 * Zones are called frames, and the openings reveals, on screen.
 *
 * With the position lock on, a picture's position is locked unless it is
 * being moved: "Unlock to move" lets the picture be dragged, zoomed and
 * turned, "Save position" locks it again and "Cancel" puts it back. A click on a
 * frame never unlocks it by itself (it did, and the owner saw pictures "automatically
 * unlock", 2026-10-17). Without the lock a stray touch on a phone, or a scroll that
 * starts on the card, moved pictures that had been lined up (the user asked for this,
 * 2026-09-27). With it off - the default with a mouse (device.tsx, 2026-10-10) - the
 * selected picture is dragged, zoomed and turned straight away.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  DEFAULT_TRANSFORM, FREE_MIN_SCALE, MAX_SCALE, covers, fillZone, frameOutlines, limitTransform, placeImage,
  type ImageRef, type ImageTransform, type ProjectDocument, type TemplateDocument, type TemplateZone, type ZoneSymbol, type ZoneText, shapeOf, withShapes,
  type ZoneShapes,
} from '@foldmycard/scene';
import { api, ApiError, type ImageItem, type ProjectItem, type VersionItem } from '../api.ts';
import { TipButton } from '../tip.tsx';
import { JourneyView, SheetFace, shownImage, useLayouts, type ShownImage } from '../card-view.tsx';
import { canShareFiles, download, makeCardPdf, makeInvitationsPdf, share } from '../card-pdf.tsx';
import { InvitePanel, codeFor, guestsOn, messageZoneOf, personalise, sampleCode, summaryOf, useEvent } from '../invite.tsx';
import { usePositionLock } from '../device.tsx';
import { saveCardFile } from '../local/card-file.ts';
import { addProblemsKey } from './new-card.tsx';
import { useFolderState } from '../safekeeping.tsx';
import { NoteEditor, newNote } from '../note-editor.tsx';
import { SYMBOL_COLOURS, SYMBOL_SHAPES, SymbolIcon, symbolsOf } from '../symbol.tsx';
import { PreviewDialog } from '../preview.tsx';
import { Link, navigate, useLocation } from '../router.tsx';
import { ago, hasString, t, type StringKey } from '../strings.ts';
import { ThemeSwitch } from '../theme.tsx';
import { SignOutButton } from '../idle.tsx';
import { UploadButton, useUploader } from './home.tsx';
import { CardStyle, type Look } from '../card-style.tsx';
import { PaletteSection, SECTIONS, SectionIcon, useOpenSections, type SectionId } from '../palette.tsx';
import { facesIn, type FaceBox } from '../faces/faces.ts';
import { lookOf, readableOn } from '../faces/look.ts';
import { faceSpot, frameFaces, planFill } from '../faces/place.ts';
import { envelopeFits } from '../envelope/designs.ts';
import { libraryGroups, type LibraryGroup, type LibraryItem } from '../library/index.ts';

/** `unsaved`: auto-save is off and there are changes waiting for Save (or Cancel). */
type SaveState = 'saved' | 'saving' | 'error' | 'conflict' | 'unsaved';
/** One press of an arrow button moves the picture this share of the zone. */
const NUDGE = 0.05;
/** One press of a shape's up / down arrow moves it this far, mm. */
const SLIDE_STEP = 0.5;

function where(z: TemplateZone, template: TemplateDocument) {
  if (z.firstSeen === 'f1') return t('whereFront');
  if (z.firstSeen === 'f2') return t('whereBack');
  // a view seen while folding it, not opening it (the twisted purse's pinwheel)
  const own = `where_${z.firstSeen}`;
  if (hasString(own)) return t(own);
  const step = template.views.findIndex((v) => v.id === z.firstSeen) - 1;
  return t('whereInside', { step });
}

/** The small picture for a zone, in the look the zone uses. */
const shownThumb = (img: ImageItem, slot: ProjectDocument['zones'][string] | undefined) =>
  (slot?.look === 'cartoon' && img.cartoon ? img.cartoon : slot?.look === 'bw' && img.bw ? img.bw : img).urls.thumb;

/** A per-browser preference; never required, so a blocked storage just means the default. */
function useStoredFlag(key: string, initial: boolean): [boolean, (v: boolean) => void] {
  const [v, setV] = useState(() => {
    try { const s = localStorage.getItem(key); return s === null ? initial : s === '1'; } catch { return initial; }
  });
  return [v, (next: boolean) => { setV(next); try { localStorage.setItem(key, next ? '1' : '0'); } catch { /* fine */ } }];
}

/** Line icons drawn here, in the text colour. */
const I = {
  zoomIn: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5L21 21M10.5 7.5v6M7.5 10.5h6" /></>,
  zoomOut: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5L21 21M7.5 10.5h6" /></>,
  lock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>,
  unlock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 7.6-1.8" /></>,
  sparkle: <><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 15l.7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7z" /></>,
  undo: <><path d="M9 14L4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 0 12h-3" /></>,
  redo: <><path d="M15 14l5-5-5-5" /><path d="M20 9H10a6 6 0 0 0 0 12h3" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  up: <path d="M6 15l6-6 6 6" />,
  down: <path d="M6 9l6 6 6-6" />,
  move: <><path d="M12 3v18M8 7l4-4 4 4M8 17l4 4 4-4" /></>,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  prev: <path d="M15 6l-6 6 6 6" />,
  next: <path d="M9 6l6 6-6 6" />,
};
const Icon = ({ k }: { k: keyof typeof I }) => <svg className="btn-icon" viewBox="0 0 24 24" aria-hidden="true">{I[k]}</svg>;

export function EditorPage({ projectId }: { projectId: string }) {
  const loc = useLocation();
  /** An invitation's event and guests (invite.tsx), when this card is one. */
  const [inviteWork, setInviteWork] = useState<string | null>(null);
  /** Pictures the new-card steps could not add (new-card.tsx), named once here. */
  const [addProblems, setAddProblems] = useState<string[]>(() => {
    try {
      const v = sessionStorage.getItem(addProblemsKey(projectId));
      return v ? JSON.parse(v) : [];
    } catch {
      return [];
    }
  });
  // said once: not again when the card is opened later
  useEffect(() => { try { sessionStorage.removeItem(addProblemsKey(projectId)); } catch { /* fine */ } }, [projectId]);
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [baseTemplate, setTemplate] = useState<TemplateDocument | null>(null);
  const [doc, setDoc] = useState<ProjectDocument | null>(null);
  /** The shapes zones 10-13 can take, and the template with this card's choices in place (ProjectDocument.shapes). */
  const [shapeCat, setShapeCat] = useState<ZoneShapes | null>(null);
  const template = useMemo(() => baseTemplate && withShapes(baseTemplate, shapeCat, doc?.shapes, doc?.slide),
    [baseTemplate, shapeCat, doc?.shapes, doc?.slide]);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [selected, setSelected] = useState(1);
  const [viewId, setViewId] = useState('f1');
  const [status, setStatus] = useState<SaveState>('saved');
  const [failed, setFailed] = useState<string | null>(null);
  /** A move or zoom in progress: shown at once, saved when the gesture ends. */
  const [draft, setDraft] = useState<{ zone: number; transform: ImageTransform } | null>(null);
  const [viewNumbers, setViewNumbers] = useStoredFlag('fmc.numbers', true);
  /** The print sheet starts without its frame numbers (asked for 2026-10-18): it shows the sheet as it prints. */
  const [sheetNumbers, setSheetNumbers] = useStoredFlag('fmc.numbers.sheet', false);
  const showNumbers = viewId === 'sheet' ? sheetNumbers : viewNumbers;
  const setShowNumbers = viewId === 'sheet' ? setSheetNumbers : setViewNumbers;
  /** Pictures move only after "Unlock to move" (phones, by default) or straight away (with a mouse). */
  const [lockPositions, setLockPositions] = usePositionLock();
  /** The right-hand tool palette's open sections (palette.tsx), and whether it is folded away to the side. */
  const palette = useOpenSections();
  const [panelShut, setPanelShut] = useStoredFlag('fmc.panelShut', false);
  const folder = useFolderState();
  const [focus, setFocus] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  /** Making / sharing the PDF, saving the card file: what to tell the person. */
  const [out, setOut] = useState<{ busy?: boolean; msg?: string; err?: string }>({});
  /** A PDF made and waiting for a tap to share (the browser wanted a fresh one). */
  const [ready, setReady] = useState<File | null>(null);
  const sharing = useMemo(() => canShareFiles(new File([''], 'card.pdf', { type: 'application/pdf' })), []);
  /** The zone whose picture is unlocked for moving, and where it was when that started. */
  const [editing, setEditing] = useState<{ zone: number; start: ImageTransform | undefined } | null>(null);
  /** Making a cartoon look: a few seconds the first time. */
  const [looking, setLooking] = useState<{ busy?: boolean; err?: string }>({});

  const versionId = loc.searchParams.get('v') || (versions.length
    ? [...versions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0].id : null);

  // project, its versions and the pictures
  useEffect(() => {
    api.project(projectId).then((r) => { setProject(r.project); setVersions(r.versions); })
      .catch((err) => setFailed(err instanceof ApiError && err.status === 404 ? 'not found' : t('somethingWrong')));
    api.images().then((r) => { setImages(r.images); setImagesLoaded(true); });
  }, [projectId]);

  /*
   * Undo, redo, "as opened", Save and Cancel (2026-10-18, the owner: "a way I can undo the design
   * or revert back whenever the user opens an old project ... a save button so the user can save
   * or cancel changes, and an auto save"). Every change keeps the card as it was in a history, so
   * Undo brings it back. With auto-save on (the default) each change is saved at once, as before;
   * with it off, changes wait for Save - or Cancel, which puts back the card as last saved. "As
   * opened" puts back the card as it was when this version was opened. Each of these is itself a
   * change, so it can be undone too.
   */
  const [autoSave, setAutoSave] = useStoredFlag('fmc.autosave', true);
  const autoRef = useRef(autoSave);
  autoRef.current = autoSave;
  const past = useRef<ProjectDocument[]>([]);
  const future = useRef<ProjectDocument[]>([]);
  /** When typing last went into the history: a pause in typing is one step of Undo, not each letter. */
  const lastTyping = useRef(0);
  const [, setHistoryTick] = useState(0);
  const historyChanged = () => setHistoryTick((n) => n + 1);
  /** The card as last saved, and as it was when opened. */
  const lastSaved = useRef<ProjectDocument | null>(null);
  const opened = useRef<{ doc: ProjectDocument; json: string; at: string } | null>(null);
  /** A card not changed for a while: a note that changes can be undone, or made on a copy. */
  const [oldNote, setOldNote] = useState<string | null>(null);
  /** The bar under the header for naming a copy, or renaming the version. */
  const [nameBar, setNameBar] = useState<{ mode: 'copy' | 'rename'; name: string } | null>(null);

  // the version being edited, and the template it is pinned to
  const revision = useRef(0);
  useEffect(() => {
    if (!project || !versionId) return;
    let live = true;
    setDoc(null);
    api.version(project.id, versionId).then(async ({ version }) => {
      const [tpl, cat] = await Promise.all([api.template(project.templateId, project.paper, version.templateHash),
        api.shapes(project.templateId, project.paper, version.templateHash)]);
      if (!live) return;
      revision.current = version.revision;
      setTemplate(tpl);
      setShapeCat(cat);
      setDoc(version.document!);
      setStatus('saved');
      lastSaved.current = version.document!;
      opened.current = { doc: version.document!, json: JSON.stringify(version.document), at: version.updatedAt };
      past.current = [];
      future.current = [];
      historyChanged();
      setNameBar(null);
      setOldNote(Date.now() - Date.parse(version.updatedAt) > 2 * 86_400_000 ? version.updatedAt : null);
    });
    return () => { live = false; };
  }, [project, versionId]);

  /* ------------------------------------------------------------- saving */

  /** The document as last rendered, for edits that land after an await. */
  const docRef = useRef(doc);
  docRef.current = doc;
  const pending = useRef<ProjectDocument | null>(null);
  const inflight = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const target = useRef({ pid: projectId, vid: versionId });
  target.current = { pid: projectId, vid: versionId };

  const flush = useCallback(async () => {
    window.clearTimeout(timer.current);
    if (inflight.current || !pending.current || !target.current.vid) return;
    inflight.current = true;
    const next = pending.current;
    pending.current = null;
    setStatus('saving');
    try {
      const r = await api.saveVersion(target.current.pid, target.current.vid, next, revision.current);
      revision.current = r.version.revision;
      lastSaved.current = next;
      setStatus(pending.current ? (autoRef.current ? 'saving' : 'unsaved') : 'saved');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setStatus('conflict');
        inflight.current = false;
        return;
      }
      pending.current = pending.current ?? next;
      setStatus('error');
      timer.current = window.setTimeout(() => void flush(), 3000);
    }
    inflight.current = false;
    if (pending.current && autoRef.current) void flush();
  }, []);

  // nothing typed is lost by leaving the page mid-sentence - with auto-save on; with it off,
  // leaving asks first (below), and changes not saved are let go
  useEffect(() => () => { if (autoRef.current) void flush(); }, [flush]);

  /** Keep the card as it is now in the history, before a change. */
  const remember = (typing = false) => {
    const d = docRef.current;
    if (!d) return;
    const now = Date.now();
    if (typing && now - lastTyping.current < 1500 && past.current.length) { lastTyping.current = now; return; }
    lastTyping.current = typing ? now : 0;
    past.current.push(d);
    if (past.current.length > 100) past.current.shift();
    future.current = [];
    historyChanged();
  };
  /** Show a card and keep it: saved at once (or after a pause in typing) with auto-save on, else waiting for Save. */
  const apply = (next: ProjectDocument, soon = false) => {
    setDoc(next);
    docRef.current = next;
    pending.current = next;
    window.clearTimeout(timer.current);
    if (!autoRef.current) { setStatus('unsaved'); return; }
    if (soon) timer.current = window.setTimeout(() => void flush(), 700);
    else void flush();
  };
  const change = (next: ProjectDocument) => {
    if (status === 'conflict') return;
    // a "change" that changes nothing (the same arrangement again) is not a step of Undo
    if (!docRef.current || JSON.stringify(next) !== JSON.stringify(docRef.current)) remember();
    apply(next);
  };
  /** The whole card in black and white, or back in colour (ProjectDocument.mono): nothing else changes. */
  const setMono = (on: boolean) => {
    const { mono: _, ...rest } = docRef.current!;
    change(on ? { ...rest, mono: true } : rest);
  };
  /** Typing: show at once, save when the typing pauses. */
  const changeSoon = (next: ProjectDocument) => {
    if (status === 'conflict') return;
    remember(true);
    apply(next, true);
  };

  const undo = () => {
    const prev = past.current.pop();
    if (!prev || !docRef.current || status === 'conflict') return;
    future.current.push(docRef.current);
    lastTyping.current = 0;
    historyChanged();
    apply(prev);
  };
  const redo = () => {
    const next = future.current.pop();
    if (!next || !docRef.current || status === 'conflict') return;
    past.current.push(docRef.current);
    lastTyping.current = 0;
    historyChanged();
    apply(next);
  };
  /** Back to the card as it was when this version was opened. */
  const asOpened = () => {
    if (!opened.current || !confirm(t('asOpenedConfirm'))) return;
    change(structuredClone(opened.current.doc));
  };
  /** Changes not saved yet: there are some, and auto-save is off. */
  const unsaved = !autoSave && !!pending.current;
  const saveNow = async () => {
    await flush();
    for (let i = 0; i < 100 && (inflight.current || pending.current); i++) await new Promise((r) => setTimeout(r, 100));
  };
  /** Let go of the changes not saved: the card as last saved comes back (and Undo can bring them back again). */
  const cancelChanges = () => {
    if (!lastSaved.current || !confirm(t('cancelConfirm'))) return;
    remember();
    pending.current = null;
    window.clearTimeout(timer.current);
    setDoc(lastSaved.current);
    docRef.current = lastSaved.current;
    setStatus('saved');
  };
  const switchAutoSave = (on: boolean) => {
    setAutoSave(on);
    autoRef.current = on;
    if (on && pending.current) void flush();
  };

  /**
   * Wait until every change is saved, so what is printed or folded is the latest card. With
   * auto-save off and changes not saved, ask first: false when the person would rather stay.
   */
  const settle = async (): Promise<boolean> => {
    if (!autoRef.current && pending.current) {
      if (!confirm(t('saveFirstConfirm'))) return false;
    }
    await saveNow();
    return true;
  };

  // with auto-save off, leaving the page (closing the tab, reloading) with changes not saved asks first
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    addEventListener('beforeunload', warn);
    return () => removeEventListener('beforeunload', warn);
  }, [unsaved]);

  // Ctrl+Z / Cmd+Z undoes, Ctrl+Y or Ctrl+Shift+Z redoes - except while typing, where the box has its own
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const el = e.target as HTMLElement;
      if (el.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if (k === 'y' || (k === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  });

  /* -------------------------------------------------------------- edits */

  const byId = useMemo(() => new Map(images.map((i) => [i.id, i])), [images]);
  const pictures = useMemo(() => {
    const m = new Map<number, ShownImage>();
    for (const [k, slot] of Object.entries(doc?.zones || {})) {
      const img = slot.image && !slot.plain && byId.get(slot.image.id);
      if (!img) continue;
      const transform = draft?.zone === Number(k) ? draft.transform : slot.transform;
      m.set(Number(k), shownImage(img, slot, transform));
    }
    return m;
  }, [doc, byId, draft]);
  const notes = useMemo(() => {
    const m = new Map<number, ZoneText>();
    for (const [k, slot] of Object.entries(doc?.zones || {})) if (slot.text && !slot.plain) m.set(Number(k), slot.text);
    return m;
  }, [doc]);
  /** The symbols in symbol frames (symbol.tsx, 2026-10-31). */
  const symbols = useMemo(() => symbolsOf(doc), [doc]);
  /** The zones left plain (ZoneSlot.plain): neither picture nor note shows in them. */
  const plain = useMemo(() => new Set(Object.entries(doc?.zones || {}).filter(([, s]) => s.plain).map(([k]) => Number(k))), [doc]);
  // an invitation: the card is shown as its first guest's copy - their name for [Name], their RSVP code
  const ev = useEvent(doc?.invitation?.eventId);
  const inviteZone = doc?.invitation?.zone;
  // an event can have several cards: this one is for its own guests
  const firstGuest = guestsOn(ev.data, project?.id)[0];
  const shownNotes = useMemo(() => (inviteZone !== undefined && firstGuest ? personalise(notes, firstGuest.name) : notes),
    [notes, inviteZone, firstGuest?.name]);
  const inviteCode = useMemo(() => (inviteZone === undefined ? undefined : firstGuest ? codeFor(inviteZone, firstGuest, ev.data?.details ?? null) : sampleCode(inviteZone)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inviteZone, firstGuest?.link, firstGuest?.name, ev.data?.details?.title]);
  const layouts = useLayouts(template || ({ zones: [] } as unknown as TemplateDocument), notes);

  /** The zones as they were before "Fill my card": its undo, offered until the next edit of a zone. */
  const [undoZones, setUndoZones] = useState<ProjectDocument['zones'] | null>(null);
  /** Arranging the pictures: what to say meanwhile, and after. */
  const [filling, setFilling] = useState<string | null>(null);
  const [fillNote, setFillNote] = useState<string | null>(null);
  /** "Another arrangement" asks the planner for a new one each time. */
  const fillSeed = useRef(0);
  /** "No face found", for the zone it was said about. */
  const [faceNote, setFaceNote] = useState<{ zone: number; msg: string } | null>(null);

  /** Rewrite one zone's slot, dropping it altogether once it holds nothing. */
  const editSlot = (zone: number, edit: (slot: ProjectDocument['zones'][string]) => void, soon = false) => {
    const doc = docRef.current;
    if (!doc) return;
    setUndoZones(null);
    setFillNote(null);
    const zones = { ...doc.zones };
    const slot = { ...(zones[zone] || { image: null }) };
    edit(slot);
    if (!slot.image && !slot.text && !slot.symbol && !slot.transform && !slot.adjust && !slot.plain) delete zones[zone];
    else zones[zone] = slot;
    (soon ? changeSoon : change)({ ...doc, zones });
  };

  const assign = (zone: number, ref: ImageRef | null) => {
    if (editing?.zone === zone) setEditing(null);
    editSlot(zone, (slot) => {
      // a move or zoom, and the look, belong to the picture they were made on
      if (slot.image?.id !== ref?.id) { delete slot.transform; delete slot.look; }
      slot.image = ref;
      // a picture chosen for a shape left plain brings the shape back
      if (ref) delete slot.plain;
    });
  };

  /**
   * Another shape for a zone (ProjectDocument.shapes, 2026-10-11) - for 10 and
   * 11 both at once. A picture there is fitted to the new shape afresh.
   */
  const setShape = (zones: number[], key: string) => {
    const d = docRef.current, entry = shapeCat?.zones[String(zones[0])];
    if (!d || !entry || !shapeCat) return;
    const wasLined = zones.length === 2 && lined(d);
    const shapes = { ...(d.shapes || {}) };
    const slots = { ...d.zones };
    for (const n of zones) {
      if (key === entry.default) delete shapes[n]; else shapes[n] = key;
      const slot = slots[n];
      if (slot?.transform) { const { transform: _, ...rest } = slot; slots[n] = rest; }
    }
    const { shapes: _old, slide: _s, ...rest } = d;
    let next: ProjectDocument = Object.keys(shapes).length ? { ...rest, zones: slots, shapes } : { ...rest, zones: slots };
    // the slides, kept within the new shape's ranges - and lined up again if they were
    const slide = { ...(d.slide || {}) };
    for (const n of zones) {
      const s = shapeOf(shapeCat, n, next.shapes, d.slide)?.slide ?? 0;
      if (s) slide[n] = s; else delete slide[n];
    }
    if (Object.keys(slide).length) next = { ...next, slide };
    if (wasLined) next = lineUp(next) ?? next;
    change(next);
  };

  /** Zones 10 and 11, lined up in Reveal 1: their centres at the same height there. */
  const pairState = (d: ProjectDocument) => {
    const pair = shapeCat?.zones['10']?.pair;
    if (!shapeCat || !pair) return null;
    const [a, b] = pair.map((n) => ({ n, got: shapeOf(shapeCat, n, d.shapes, d.slide)!, y0: shapeCat.zones[String(n)].y0 ?? 0 }));
    return { a, b, gap: (b.y0 + b.got.slide) - (a.y0 + a.got.slide) };
  };
  const lined = (d: ProjectDocument) => Math.abs(pairState(d)?.gap ?? 1) < 0.3;
  /** Line the two halves up: the first stays where it is if it can, the second comes to it. Null if they cannot meet. */
  const lineUp = (d: ProjectDocument): ProjectDocument | null => {
    const p = pairState(d);
    if (!p) return null;
    const ra = p.a.got.option.slide, rb = p.b.got.option.slide;
    if (!ra || !rb) return null;
    const D = p.a.y0 - p.b.y0;                       // lined up: slide b = slide a + D
    const lo = Math.max(ra.min, rb.min - D), hi = Math.min(ra.max, rb.max - D);
    if (lo > hi) return null;
    const sa = Math.min(hi, Math.max(lo, p.a.got.slide)), sb = sa + D;
    const slide = { ...(d.slide || {}), [p.a.n]: Math.round(sa * 10) / 10, [p.b.n]: Math.round(sb * 10) / 10 };
    return { ...d, slide };
  };
  /** Slide one half (mm, + = down in Reveal 1), kept within its range; saved when the hand rests. */
  const setSlide = (n: number, mm: number) => {
    const d = docRef.current;
    const r = shapeCat && d ? shapeOf(shapeCat, n, d.shapes, d.slide)?.option.slide : null;
    if (!d || !r) return;
    const to = Math.round(Math.min(r.max, Math.max(r.min, mm)) * 10) / 10;
    const slide: Record<string, number> = { ...(d.slide || {}), [n]: to };
    if (!to) delete slide[n];
    const { slide: _, ...rest } = d;
    changeSoon(Object.keys(slide).length ? { ...rest, slide } : rest);
  };
  /** Dragging a half on the card ("Move the shape"): where it was when the drag began. */
  const slideFrom = useRef<number | null>(null);
  const [shapeDrag, setShapeDrag] = useState(false);
  const onSlide = (n: number, down: number, done: boolean) => {
    const d = docRef.current;
    if (!d || !shapeCat) return;
    if (slideFrom.current === null) slideFrom.current = shapeOf(shapeCat, n, d.shapes, d.slide)?.slide ?? 0;
    setSlide(n, slideFrom.current + down);
    if (done) { slideFrom.current = null; void flush(); }
  };

  /** Leave a shape plain - the paper covers it - or bring it back (ZoneSlot.plain, 2026-10-11). */
  const setPlain = (zone: number, on: boolean) => {
    if (editing?.zone === zone) setEditing(null);
    editSlot(zone, (slot) => {
      if (on) slot.plain = true;
      else delete slot.plain;
    });
  };

  /** Save a move / zoom / turn; the default fit is stored as no adjustment at all. */
  const setTransform = (zone: number, tr: ImageTransform | null) => editSlot(zone, (slot) => {
    const plain = !tr || (tr.scale === 1 && tr.offsetX === 0 && tr.offsetY === 0 && tr.rotationDeg === 0);
    if (plain) delete slot.transform;
    else slot.transform = tr;
  });
  const onAdjust = (zone: number, tr: ImageTransform, done: boolean) => {
    if (done) {
      setDraft(null);
      setTransform(zone, tr);
    } else setDraft({ zone, transform: tr });
  };

  /* the position lock */
  const startEditing = (zone = selected) => setEditing({ zone, start: doc?.zones[zone]?.transform });
  const cancelEditing = () => {
    if (!editing) return;
    setTransform(editing.zone, editing.start ?? null);
    setEditing(null);
  };
  // choosing another zone locks the one being edited, keeping where it was put
  useEffect(() => { setEditing((e) => (e && e.zone !== selected ? null : e)); setShapeDrag(false); }, [selected]);

  /** Make a picture's cartoon or black-and-white copy, on this device, if it has none yet. */
  const ensureLook = async (id: string, look: Look) => {
    const img = byId.get(id);
    if (!img || look === 'photo' || (look === 'cartoon' ? img.cartoon : img.bw)) return;
    const made = look === 'cartoon' ? await api.cartoon(id) : await api.blackWhite(id);
    setImages((l) => l.map((i) => (i.id === made.id ? made : i)));
  };

  /** Photo, cartoon or black and white: the copies are made on this device the first time they are asked for. */
  const setLook = async (look: Look) => {
    const ref = doc?.zones[selected]?.image;
    if (!ref) return;
    setLooking({});
    if (look === 'photo') return editSlot(selected, (slot) => { delete slot.look; });
    const zone = selected;
    setLooking({ busy: true });
    try {
      await ensureLook(ref.id, look);
    } catch (err) {
      return setLooking({ err: (err as Error).message });
    }
    setLooking({});
    editSlot(zone, (slot) => { if (slot.image?.id === ref.id) slot.look = look; });
  };

  /** One look for every picture on the card (2026-10-10) - black and white saves the colour ink. */
  const [allBusy, setAllBusy] = useState<string | null>(null);
  const setAllLooks = async (look: Look) => {
    const d = docRef.current;
    if (!d) return;
    const ids = [...new Set(Object.values(d.zones).map((s) => s.image?.id).filter((x): x is string => !!x))];
    try {
      for (let i = 0; i < ids.length; i++) {
        if (look !== 'photo') setAllBusy(t('lookMakingN', { n: i + 1, of: ids.length }));
        await ensureLook(ids[i], look);
      }
    } catch (err) {
      setAllBusy(null);
      return setLooking({ err: (err as Error).message });
    }
    setAllBusy(null);
    const now = docRef.current!;
    const zones = Object.fromEntries(Object.entries(now.zones).map(([k, slot]) => {
      if (!slot.image) return [k, slot];
      const { look: _, ...rest } = slot;
      return [k, look === 'photo' ? rest : { ...rest, look }];
    }));
    change({ ...now, zones });
  };

  /** The library: the drawings, and the administrator's photographs once they arrive. */
  const [library, setLibrary] = useState<LibraryGroup[]>([]);
  useEffect(() => { api.library().then((r) => setLibrary(libraryGroups(r.items))).catch(() => {}); }, []);

  /** A drawing or photograph from the picture library: made into a picture of this card, then put in the zone. */
  const [libraryBusy, setLibraryBusy] = useState<string | null>(null);
  const pickLibrary = async (item: LibraryItem) => {
    if (!project) return;
    const zone = selected;
    setLibraryBusy(item.id);
    try {
      const img = await api.addFromLibrary(item, project.id);
      setImages((l) => (l.some((i) => i.id === img.id) ? l : [img, ...l]));
      assign(zone, { source: 'upload', id: img.id });
    } catch (err) {
      setLooking({ err: (err as Error).message });
    } finally {
      setLibraryBusy(null);
    }
  };

  const setNote = (zone: number, note: ZoneText | null) => editSlot(zone, (slot) => {
    if (note) slot.text = note;
    else delete slot.text;
    // writing in a shape left plain brings it back
    if (note) delete slot.plain;
  }, true);
  /** The symbol in a symbol frame (symbol.tsx, 2026-10-31); null takes it out. */
  const setSymbol = (zone: number, symbol: ZoneSymbol | null) => editSlot(zone, (slot) => {
    if (symbol) slot.symbol = symbol;
    else delete slot.symbol;
    if (symbol) delete slot.plain;
  }, true);

  const uploader = useUploader((img) => {
    setImages((l) => [img, ...l]);
    const d = docRef.current;
    if (d && popupZone.current !== null && !d.zones[popupZone.current]?.image) assign(popupZone.current, { source: 'upload', id: img.id });
  }, projectId);

  /**
   * This card's pictures - added to it, or on it - and only those: each card
   * has its own, and never shows another card's (2026-10-10).
   */
  const usedHere = useMemo(() => new Set(Object.values(doc?.zones || {}).map((s) => s.image?.id).filter(Boolean)), [doc]);
  const mine = images.filter((i) => i.projectId === projectId || usedHere.has(i.id));
  const [pictureNotice, setPictureNotice] = useState<string | null>(null);

  /** "From another card" (2026-10-11): the other cards and their pictures, to copy into this one. */
  const [importing, setImporting] = useState(false);
  const [cards, setCards] = useState<ProjectItem[]>([]);
  const [imported, setImported] = useState<string | null>(null);
  useEffect(() => {
    if (importing) api.projects().then((r) => setCards(r.projects.filter((p) => p.id !== projectId))).catch(() => {});
  }, [importing, projectId]);
  const otherCards = useMemo(() => cards
    .map((card) => ({ card, pics: images.filter((i) => i.projectId === card.id) }))
    .filter((x) => x.pics.length), [cards, images]);
  /**
   * "My other pictures" in the frame's pop-up (2026-10-17): every picture on this device that
   * is not on this card - other cards', and any added before cards kept their own - each once.
   */
  const others = useMemo(() => {
    const here = new Set(mine.flatMap((i) => [i.id, i.copiedFrom].filter(Boolean)));
    const seen = new Set<string>();
    return images.filter((i) => {
      const root = i.copiedFrom ?? i.id;
      if (i.library || here.has(i.id) || here.has(root) || seen.has(root)) return false;
      seen.add(root);
      return true;
    });
  }, [images, mine]);
  const [copying, setCopying] = useState<string | null>(null);
  /** Copy a picture of another card into this one, and put it in the frame. */
  const takeOther = async (img: ImageItem) => {
    const zone = selected;
    setCopying(img.id);
    try {
      const [made] = await api.copyPictures([img.id], projectId);
      if (made) {
        setImages((l) => [made, ...l]);
        assign(zone, { source: 'upload', id: made.id });
      }
    } finally {
      setCopying(null);
    }
  };
  /** Copies already here: by the picture they were first copied from. */
  const copiedHere = useMemo(() => new Set(mine.flatMap((i) => (i.copiedFrom ? [i.id, i.copiedFrom] : [i.id]))), [mine]);
  const importPictures = async (ids: string[]) => {
    const made = await api.copyPictures(ids, projectId);
    setImages((l) => [...made, ...l]);
    setImported(t('importedN', { n: made.length }));
  };
  const removePicture = async (img: ImageItem) => {
    setPictureNotice(null);
    if (usedHere.has(img.id)) return setPictureNotice(t('pictureOnThisCard'));
    try {
      await api.deleteImage(img.id);
      setImages((l) => l.filter((x) => x.id !== img.id));
    } catch (err) {
      // on another version of this card
      setPictureNotice(err instanceof ApiError && err.status === 409 ? t('pictureOnOtherVersion') : (err as Error).message);
    }
  };

  const zone = template?.zones.find((z) => z.number === selected) || null;
  const current = doc?.zones[selected]?.image || null;
  const visibleHere = !template || viewId === 'sheet' ||
    !!template.views.find((v) => v.id === viewId)?.pieces.some((p) => p.zone === selected);

  /** Select from a list: if the zone does not show in this view, go to where it first does. */
  const pick = (n: number) => {
    setSelected(n);
    const z = template?.zones.find((x) => x.number === n);
    const view = template?.views.find((v) => v.id === viewId);
    if (z?.firstSeen && view && !view.pieces.some((p) => p.zone === n)) setViewId(z.firstSeen);
  };

  /** The frame's pop-up (2026-10-11, with its pictures since 2026-10-17): a click on a frame opens it. */
  const [popup, setPopup] = useState(false);
  /** The frame the pop-up is for, for pictures that arrive after a wait (an upload). */
  const popupZone = useRef<number | null>(null);
  popupZone.current = popup ? selected : null;
  const [popTab, setPopTab] = useState<'card' | 'mine' | 'library'>('card');
  /** A frame clicked - on the card, or in a list: chosen and its pop-up open. It stays locked until "Unlock to move". */
  const choose = (n: number, fromList = false) => {
    if (fromList) pick(n); else setSelected(n);
    setPopup(true);
  };
  /** Closed: every move already saved itself; with the lock on, the picture is locked again. */
  const closePopup = () => {
    setEditing(null);
    setShapeDrag(false);
    setPopup(false);
  };
  useEffect(() => {
    if (!popup) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !(e.target as HTMLElement).closest?.('input, textarea, select')) closePopup();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup]);

  /** Zoom in on a frame (the card's corner, the pop-up, a double click) - or back out to the whole card. */
  const zoomTo = (n: number) => {
    if (focus && n === selected) return setFocus(false);
    setSelected(n);
    setFocus(true);
  };

  /**
   * A copy of this version, under the name given. With changes not saved (auto-save off), the
   * copy takes them, and this version stays as it was last saved: a way to try something out
   * without touching the original.
   */
  const duplicate = async (label: string) => {
    if (!project || !versionId) return;
    const withChanges = !autoRef.current && pending.current ? docRef.current! : undefined;
    if (!withChanges) await saveNow();
    pending.current = null;
    const r = await api.duplicateVersion(project.id, versionId, { label, document: withChanges });
    setVersions((l) => [...l, r.version]);
    setNameBar(null);
    navigate(`/projects/${project.id}?v=${r.version.id}`);
  };
  const renameVersion = async (label: string) => {
    if (!project || !versionId) return;
    const r = await api.renameVersion(project.id, versionId, label);
    setVersions((l) => l.map((v) => (v.id === r.version.id ? { ...v, label: r.version.label } : v)));
    setNameBar(null);
  };
  /** Another version on screen - after asking, if there are changes not saved. */
  const openVersion = (id: string) => {
    if (unsaved && !confirm(t('leaveUnsaved'))) return;
    pending.current = null;
    navigate(`/projects/${project!.id}?v=${id}`);
  };

  /** Delete the version on screen, after asking; a card keeps at least one (the API refuses the last). */
  const removeVersion = async () => {
    if (!project || !versionId || versions.length < 2) return;
    const v = versions.find((x) => x.id === versionId);
    if (!confirm(t('confirmDeleteVersion', { label: v?.label ?? '' }))) return;
    await api.deleteVersion(project.id, versionId);
    const rest = versions.filter((x) => x.id !== versionId);
    setVersions(rest);
    navigate(`/projects/${project.id}?v=${rest[rest.length - 1].id}`, { replace: true });
  };

  const rename = async (title: string) => {
    if (project && title.trim() && title !== project.title) {
      setProject((await api.renameProject(project.id, title)).project);
    }
  };

  /** The faces in each of these pictures, found on this device (faces/faces.ts). */
  const findFaces = async (list: ImageItem[]) => {
    const out = new Map<string, FaceBox[]>();
    for (let i = 0; i < list.length; i++) {
      setFilling(t('facesLooking', { n: i + 1, of: list.length }));
      out.set(list[i].id, await facesIn(list[i].id, list[i].urls.preview).catch(() => []));
    }
    return out;
  };
  /** The frames "Fill my card" may fill: every one not left plain. */
  // frames for words only (the fortune teller's numbers and fortunes) take no picture
  const fillable = (d: ProjectDocument) => template?.zones.filter((z) => !z.textOnly && !z.symbolOnly && !d.zones[z.number]?.plain) ?? [];
  /**
   * "Fill my card" (2026-10-11; one button and smarter since 2026-10-17): this card's
   * pictures into the empty frames - or, once all are full, another arrangement of
   * them all (faces/place.ts). The message frame gets the calmest picture, and its
   * message the colour that reads on it (faces/look.ts).
   */
  const autoFill = async (mode: 'empty' | 'all') => {
    if (!docRef.current || !template || filling) return;
    if (!mine.length) { setFillNote(t('fillNoPictures')); return; }
    setFillNote(null);
    try {
      const faces = await findFaces(mine);
      setFilling(t('fillWorking'));
      const looks = new Map(await Promise.all(mine.map(async (i) => [i.id, await lookOf(i.id, i.urls.thumb)] as const)));
      const before = docRef.current!;
      if (mode === 'all') fillSeed.current += 1;
      // pictures in frames that stay as they are count as used
      const staying = new Set(Object.entries(before.zones)
        .filter(([, s]) => s.image && (mode === 'empty' || s.plain)).map(([, s]) => s.image!.id));
      const plan = planFill(template, before, mine.map((i) => ({
        ref: { source: 'upload' as const, id: i.id }, ...i.sizes.preview, faces: faces.get(i.id) ?? [], used: staying.has(i.id), look: looks.get(i.id),
        library: !!i.library,
      })), { mode, seed: mode === 'all' ? fillSeed.current : 0 });
      if (!plan.length) { setFillNote(t('fillAll')); return; }
      const zones = { ...before.zones };
      for (const p of plan) {
        const { transform: _, look: __, ...rest } = zones[p.zone] || { image: null };
        const slot = p.transform ? { ...rest, image: p.ref, transform: p.transform } : { ...rest, image: p.ref };
        // a message on the picture: written so it reads there
        const z = template.zones.find((x) => x.number === p.zone);
        const look = looks.get(p.ref.id);
        if (slot.text && look && z?.defaultContent === 'text') slot.text = readableOn(slot.text, look);
        zones[p.zone] = slot;
      }
      change({ ...before, zones });
      setUndoZones(before.zones);
      setFillNote(plan.length === 1 ? t('fillDoneOne') : t('fillDone', { n: plan.length }));
    } finally {
      setFilling(null);
    }
  };
  const undoFill = () => {
    if (!undoZones || !docRef.current) return;
    change({ ...docRef.current, zones: undoZones });
    setUndoZones(null);
    setFillNote(null);
  };
  /** One zone's picture: its faces moved to the middle and sized to fit. */
  const centreFace = async (zoneNo: number) => {
    const z = template?.zones.find((x) => x.number === zoneNo);
    const ref = docRef.current?.zones[zoneNo]?.image;
    const img = ref && byId.get(ref.id);
    if (!z || !img || filling) return;
    setFilling(t('facesLookingOne'));
    try {
      const tr = frameFaces(z, img.sizes.preview, await facesIn(img.id, img.urls.preview).catch(() => []), template && faceSpot(template, z));
      if (tr) setTransform(zoneNo, tr);
      setFaceNote(tr ? null : { zone: zoneNo, msg: t('facesNone') });
    } finally {
      setFilling(null);
    }
  };

  // a card made with "Fill it for me" (new-card.tsx) arrives with ?fill=1: fill it once, as it opens
  const fillOnOpen = useRef(loc.searchParams.get('fill') === '1');
  useEffect(() => {
    if (!fillOnOpen.current || !doc || !template || !imagesLoaded || !project) return;
    fillOnOpen.current = false;
    navigate(`/projects/${project.id}${versionId && loc.searchParams.get('v') ? `?v=${versionId}` : ''}`, { replace: true });
    void autoFill('empty');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, template, imagesLoaded, project]);

  // a card made for an event (new-card.tsx), or opened from the events board, arrives with ?invite=1:
  // its guests and answers are what the host came for, so the Invitation tool opens
  const inviteOnOpen = useRef(loc.searchParams.get('invite') === '1');
  useEffect(() => {
    if (!inviteOnOpen.current || !doc || !template || !project) return;
    inviteOnOpen.current = false;
    setPanelShut(false);
    palette.show('invite');
    // ?fill=1 takes the address back itself, once the pictures are in
    if (!fillOnOpen.current) navigate(`/projects/${project.id}${versionId && loc.searchParams.get('v') ? `?v=${versionId}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, template, project]);

  /* ------------------------------------------------------------- render */

  if (failed) return <main className="page"><p className="error">{failed}</p><Link href="/" className="button back">← {t('myCards')}</Link></main>;
  if (!project || !template || !doc) return <main className="page"><p className="muted">{t('loading')}</p></main>;

  const count = template.zones.length;
  /** A frame holds something: a picture - or, in a frame for words only, words; in a symbol frame, its symbol. */
  const filled = (z: TemplateZone) => {
    const s = doc.zones[z.number];
    return !!(s?.image || (z.textOnly && s?.text?.content.trim()) || (z.symbolOnly && s?.symbol));
  };
  const view = template.views.find((v) => v.id === viewId);
  const zoomed = focus && visibleHere;
  const shown = pictures.get(selected);
  // only the selected picture can be dragged or zoomed on the card - and, with the lock on, only while it is being edited
  const moving = !!shown && (lockPositions ? editing?.zone === selected : true);
  /** Zones 10 and 11 in the view where they meet: dragging moves the shape, when asked. */
  const meetView = shapeCat?.meetView ?? 'f3';
  const slideHere = shapeDrag && viewId === meetView;
  const cardProps = { template, pictures, notes: shownNotes, symbols, invite: inviteCode, paper: doc.paper, mono: doc.mono, plain, selected, onSelect: (n: number) => choose(n),
    onAdjust: moving && !slideHere ? onAdjust : undefined, onZoom: zoomTo, placement: 'free' as const, showNumbers, focus: zoomed };
  const transform = shown?.transform || DEFAULT_TRANSFORM;
  const outlines = zone ? frameOutlines(zone) : [];
  const blank = !!(zone && shown && !covers(outlines, placeImage(outlines, zone.up, shown, transform), shown));
  const adjustTo = (patch: Partial<ImageTransform>) => {
    if (!zone || !shown) return;
    setTransform(selected, limitTransform(outlines, zone.up, shown, transform, { ...transform, ...patch }, 'free'));
  };
  /**
   * Move the picture a step (5% of the zone) as the card is read: up, right,
   * down or left - easier than a fine drag on a phone (asked for 2026-10-10).
   */
  const nudge = (dx: number, dy: number) =>
    adjustTo({ offsetX: transform.offsetX + dx * NUDGE, offsetY: transform.offsetY + dy * NUDGE });
  const arrows = (
    <span className="arrows" role="group" aria-label={t('movePicture')}>
      <button aria-label={t('moveLeft')} title={t('moveLeft')} onClick={() => nudge(-1, 0)}>←</button>
      <button aria-label={t('moveUp')} title={t('moveUp')} onClick={() => nudge(0, -1)}>↑</button>
      <button aria-label={t('moveDown')} title={t('moveDown')} onClick={() => nudge(0, 1)}>↓</button>
      <button aria-label={t('moveRight')} title={t('moveRight')} onClick={() => nudge(1, 0)}>→</button>
    </span>
  );
  const note = doc.zones[selected]?.text || null;
  const isPlain = !!doc.zones[selected]?.plain;
  /** The shapes the selected zone can take (zones 10-13), and the one it has. */
  const shapeEntry = shapeCat?.zones[String(selected)] ?? null;
  const shapeNow = shapeEntry && shapeCat ? shapeOf(shapeCat, selected, doc.shapes, doc.slide)?.option ?? null : null;
  /** Zones 10 and 11: each half's shape and slide, and where it sits in Reveal 1. */
  const pair = shapeEntry?.pair && shapeCat
    ? shapeEntry.pair.map((n) => ({ n, got: shapeOf(shapeCat, n, doc.shapes, doc.slide)!, y0: shapeCat.zones[String(n)].y0 ?? 0 }))
    : null;
  const mySlide = pair?.find((p) => p.n === selected)?.got ?? null;
  const shapeName = (k: string) => t(`shape_${k}` as StringKey);
  /** Zones 10 and 11: the option for a kind and a size - or the normal size if there is no bigger one. */
  const pairOption = (kind: string, sizeName: string) => shapeEntry?.options.find((o) => o.kind === kind && o.sizeName === sizeName)
    ?? shapeEntry?.options.find((o) => o.kind === kind && o.sizeName === 'normal');
  const emptyFrames = fillable(doc).filter((z) => !doc.zones[z.number]?.image).length;
  const isMessage = zone?.defaultContent === 'text';
  /** Open a section of the tool panel (and the panel, if it was folded away). */
  const showSection = (id: SectionId) => { setPanelShut(false); palette.show(id); };

  const printCard = async () => {
    if (!(await settle())) return;
    window.open(`/print/${project.id}?v=${versionId}`, '_blank');
  };
  const foldGuide = async () => {
    if (!(await settle())) return;
    navigate(`/projects/${project.id}/fold?v=${versionId}`);
  };
  /** The PDF, made on this device from what is on screen - fold guides included (changes not saved too). */
  const cardPdf = async () => {
    if (autoRef.current) await settle();
    const guides = await api.guides(project.templateId, project.paper, template.geometryHash);
    return makeCardPdf({ template, pictures, notes: shownNotes, symbols, guides, paper: doc.paper, mono: doc.mono, plain, invite: inviteCode }, project.title);
  };
  /** The card becomes an invitation, or stops being one (invite.tsx). */
  const linkInvitation = (invitation: { eventId: string; zone: number } | undefined) => {
    const { invitation: _, ...rest } = docRef.current!;
    change(invitation ? { ...rest, invitation } : rest);
  };
  /** The invitation's words, in the message frame: asked first when they would replace other words. */
  const writeInvitation = (text: string) => {
    const z = docRef.current!.invitation?.zone ?? messageZoneOf(template);
    if (z === null) return;
    const cur = docRef.current!.zones[z]?.text;
    if (cur?.content.trim() && cur.content !== text && !confirm(t('inviteReplaceConfirm'))) return;
    setNote(z, { ...(cur ?? newNote()), content: text });
    setSelected(z);
  };
  /** Every guest's card, on the print page (print.tsx prints one per guest for an invitation). */
  const printInvitations = () => void printCard();
  /** Every guest's card in one PDF. */
  const downloadInvitations = async () => {
    if (!ev.data || inviteZone === undefined) return;
    if (autoRef.current) await settle();
    const guests = guestsOn(ev.data, project.id);
    const of = guests.length;
    setInviteWork(t('inviteMaking', { n: 0, of }));
    try {
      const guides = await api.guides(project.templateId, project.paper, template.geometryHash);
      const cards = guests.map((g) => ({
        template, pictures, notes: personalise(notes, g.name), symbols, guides, paper: doc.paper, mono: doc.mono, plain, invite: codeFor(inviteZone, g, ev.data!.details),
      }));
      const pdf = await makeInvitationsPdf(cards, `${project.title} - ${t('invitations')}`, (n) => setInviteWork(t('inviteMaking', { n, of })));
      download(pdf, pdf.name);
    } catch (err) {
      setOut({ err: (err as Error).message });
    } finally {
      setInviteWork(null);
    }
  };
  const downloadPdf = async () => {
    setOut({ busy: true, msg: t('sending') });
    setReady(null);
    try {
      const pdf = await cardPdf();
      download(pdf, pdf.name);
      setOut({});
    } catch (err) {
      setOut({ err: (err as Error).message });
    }
  };
  const shareFile = async (pdf: File) => {
    const r = await share(pdf, project.title);
    if (r === 'needs-tap') return setReady(pdf);
    setReady(null);
    if (r === 'unsupported') {
      download(pdf, pdf.name);
      setOut({ msg: t('shareUnsupported') });
    }
  };
  const sharePdf = async () => {
    setOut({ busy: true, msg: t('sending') });
    setReady(null);
    try {
      const pdf = await cardPdf();
      setOut({});
      await shareFile(pdf);
    } catch (err) {
      setOut({ err: (err as Error).message });
    }
  };
  const saveCard = async () => {
    if (!(await settle())) return;
    setOut({ busy: true, msg: t('savingCard') });
    try {
      const { blob, name } = await saveCardFile(project.id);
      download(blob, name);
      setOut({ msg: t('cardSaved') });
    } catch (err) {
      setOut({ err: (err as Error).message });
    }
  };

  /** The pictures to choose from, in the pop-up: this card's, or the library's. */
  const pickGrid = (
    <div className="pop-pics">
      <div className="pop-pics-head">
        <strong>{t('popPictures')}</strong>
        <span className="seg" role="group" aria-label={t('popPictures')}>
          <button aria-pressed={popTab === 'card'} onClick={() => setPopTab('card')}>{t('popThisCard')}</button>
          <button aria-pressed={popTab === 'mine'} onClick={() => setPopTab('mine')}>{t('popMine')}</button>
          <button aria-pressed={popTab === 'library'} onClick={() => setPopTab('library')}>{t('popLibrary')}</button>
        </span>
      </div>
      {popTab === 'card' ? (
        <>
          <ul className="pick-grid">
            <li><UploadButton uploader={uploader} className="pick-add" /></li>
            {mine.map((img) => (
              <li key={img.id}>
                <button className="thumb-button" aria-pressed={current?.id === img.id} title={img.name || undefined}
                  onClick={() => assign(selected, { source: 'upload', id: img.id })}>
                  <img src={img.urls.thumb} alt={img.name || ''} loading="lazy" />
                </button>
              </li>
            ))}
          </ul>
          {mine.length === 0 && <p className="muted small">{t('popNoPictures')}</p>}
          {uploader.errors.map((e) => <p key={e} className="error small">{e}</p>)}
        </>
      ) : popTab === 'mine' ? (
        <div className="pick-library">
          {others.length === 0 ? <p className="muted small">{t('popMineNone')}</p> : (
            <>
              <p className="muted small">{t('popMineHint')}</p>
              <ul className="pick-grid">
                {others.map((img) => (
                  <li key={img.id}>
                    <button className="thumb-button" title={img.name || undefined} disabled={!!copying}
                      aria-busy={copying === img.id} onClick={() => void takeOther(img)}>
                      <img src={img.urls.thumb} alt={img.name || ''} loading="lazy" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ) : (
        <div className="pick-library">
          {library.map((g) => (
            <div key={g.id} className="pal-group">
              <h5>{g.name}</h5>
              <ul className="pick-grid">
                {g.items.map((item) => (
                  <li key={item.id}>
                    <button className="thumb-button" title={item.name} disabled={!!libraryBusy}
                      aria-busy={libraryBusy === item.id} onClick={() => void pickLibrary(item)}>
                      <img src={item.thumb ?? item.url} alt={item.name} loading="lazy" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /** A symbol frame's picker (the square letter's points, 2026-10-31): a heart, a star or a half moon, and its colour. */
  const symbolNow = zone?.symbolOnly ? doc.zones[selected]?.symbol ?? null : null;
  const symbolColour = symbolNow?.color ?? zone?.defaultSymbol?.color ?? SYMBOL_COLOURS[0];
  const symbolPicker = zone?.symbolOnly && (
    <>
      <span className="seg wrap" role="group" aria-label={t('palShape')}>
        {SYMBOL_SHAPES.map((s) => (
          <button key={s} aria-pressed={symbolNow?.shape === s} onClick={() => setSymbol(selected, { shape: s, color: symbolColour })}>
            <SymbolIcon shape={s} color={symbolColour} /> {t(`symbol_${s}` as StringKey)}
          </button>
        ))}
        <button aria-pressed={!symbolNow} onClick={() => setSymbol(selected, null)}>{t('symbolNone')}</button>
      </span>
      {symbolNow && (
        <span className="swatches" role="group" aria-label={t('colour')}>
          {SYMBOL_COLOURS.map((c) => (
            <button key={c} className="swatch" style={{ background: c }} aria-pressed={symbolNow.color.toLowerCase() === c}
              aria-label={c} onClick={() => setSymbol(selected, { ...symbolNow, color: c })} />
          ))}
          <input type="color" value={symbolNow.color} aria-label={t('otherColour')}
            onChange={(e) => setSymbol(selected, { ...symbolNow, color: e.target.value })} />
        </span>
      )}
      <p className="muted small">{t('symbolHint')}</p>
    </>
  );

  /** Everything about the frame itself, in the pop-up: look, position, shape, message. */
  const frameTools = zone && (
    <div className="pop-tools">
      {symbolPicker && !isPlain && (
        <div className="pop-block">
          <span className="small strong">{t('palShape')}</span>
          {symbolPicker}
        </div>
      )}
      {isPlain ? (
        <div className="pop-block">
          <p className="small muted">{t('plainHint')}</p>
          <button className="primary" onClick={() => setPlain(selected, false)}>{t('plainUndo')}</button>
        </div>
      ) : shown ? (
        <>
          <div className="pop-block">
            <span className="seg" role="group" aria-label={t('palLookHere')}>
              <button aria-pressed={!doc.zones[selected]?.look || doc.zones[selected]?.look === 'photo'}
                onClick={() => setLook('photo')}>{t('lookPhoto')}</button>
              <button aria-pressed={doc.zones[selected]?.look === 'cartoon'} onClick={() => setLook('cartoon')} disabled={looking.busy}>{t('lookCartoon')}</button>
              <button aria-pressed={doc.zones[selected]?.look === 'bw'} onClick={() => setLook('bw')} disabled={looking.busy}>{t('lookBw')}</button>
            </span>
            {looking.busy && <p className="muted small" role="status">{t('lookMaking')}</p>}
            {looking.err && <p className="error small">{looking.err}</p>}
          </div>
          {!moving ? (
            <div className="pop-block lock-row">
              <span className="locked-label"><Icon k="lock" />{t('positionLocked')}</span>
              <button className="primary" onClick={() => startEditing()}><Icon k="unlock" />{t('unlockToMove')}</button>
            </div>
          ) : (
            <div className="pop-block">
              <div className="pop-row">
                <span className="small pop-label">{t('zoom')}</span>
                <button aria-label={t('zoomOut')} title={t('zoomOut')} onClick={() => adjustTo({ scale: transform.scale / 1.1 })}>−</button>
                <input type="range" aria-label={t('zoom')} min={FREE_MIN_SCALE * 100} max={MAX_SCALE * 100} step={1}
                  value={Math.round(transform.scale * 100)} onChange={(e) => adjustTo({ scale: Number(e.target.value) / 100 })} />
                <button aria-label={t('zoomIn')} title={t('zoomIn')} onClick={() => adjustTo({ scale: transform.scale * 1.1 })}>+</button>
              </div>
              <div className="pop-row">
                <span className="small pop-label">{t('rotate')}</span>
                <button aria-label={t('pictureTurnLeft')} title={t('pictureTurnLeft')} onClick={() => adjustTo({ rotationDeg: transform.rotationDeg - 90 })}>↺</button>
                <input type="range" aria-label={t('rotate')} min={-180} max={180} step={1} value={Math.round(transform.rotationDeg)}
                  onChange={(e) => adjustTo({ rotationDeg: Number(e.target.value) })} />
                <button aria-label={t('pictureTurnRight')} title={t('pictureTurnRight')} onClick={() => adjustTo({ rotationDeg: transform.rotationDeg + 90 })}>↻</button>
              </div>
              <div className="pop-row">
                <span className="small pop-label">{t('movePicture')}</span>
                {arrows}
              </div>
              {blank && (
                <p className="warn small">{t('blankWarning')}{' '}
                  <button onClick={() => setTransform(selected, fillZone(outlines, zone.up, shown, transform))}>{t('fillZone')}</button>
                </p>
              )}
              {filling && <p className="muted small" role="status">{filling}</p>}
              {faceNote?.zone === selected && !filling && <p className="muted small" role="status">{faceNote.msg}</p>}
              <p className="muted small">{t('popHint')}</p>
              <div className="row">
                <button className="small" disabled={!!filling} onClick={() => void centreFace(selected)}>{t('centreFace')}</button>
                <button className="small" onClick={() => setTransform(selected, null)} disabled={!doc.zones[selected]?.transform}>{t('resetPicture')}</button>
                {lockPositions && (
                  <>
                    <button className="primary small" onClick={() => setEditing(null)}><Icon k="lock" />{t('savePosition')}</button>
                    <button className="small" onClick={cancelEditing}>{t('cancel')}</button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      ) : null}

      {/* the shape: its kind (zones 10-13); for the two halves, up or down in Reveal 1 */}
      {shapeEntry && shapeNow && !isPlain && (
        <div className="pop-block">
          <span className="small strong">{t('palShape')}</span>
          <span className="seg wrap" role="group" aria-label={t('palShape')}>
            {shapeEntry.pair
              ? [...new Set(shapeEntry.options.map((o) => o.kind))].map((k) => (
                <button key={k} aria-pressed={shapeNow.kind === k}
                  onClick={() => { const o = pairOption(k, shapeNow.sizeName!); if (o) setShape(shapeEntry.pair!, o.key); }}>
                  {t(`shapePair_${k}` as StringKey)}
                </button>
              ))
              : shapeEntry.options.map((o) => (
                <button key={o.key} aria-pressed={shapeNow.key === o.key} onClick={() => setShape([selected], o.key)}>{shapeName(o.kind)}</button>
              ))}
          </span>
          {pair && mySlide?.option.slide && (
            <div className="slide-quick">
              <span className="small">{t('moveShape')}</span>
              <span className="row">
                <button aria-label={t('shapeUp')} title={t('shapeUp')} disabled={mySlide.slide <= mySlide.option.slide.min}
                  onClick={() => { setSlide(selected, mySlide.slide - SLIDE_STEP); }}><Icon k="up" /></button>
                <button aria-label={t('shapeDown')} title={t('shapeDown')} disabled={mySlide.slide >= mySlide.option.slide.max}
                  onClick={() => { setSlide(selected, mySlide.slide + SLIDE_STEP); }}><Icon k="down" /></button>
                <button aria-pressed={shapeDrag} title={t('moveShapeHint')}
                  onClick={() => { if (viewId !== meetView) setViewId(meetView); setShapeDrag(!shapeDrag); }}>
                  <Icon k="move" />{t('moveShape')}
                </button>
                <button onClick={() => { const next = lineUp(doc); if (next) change(next); }} disabled={!lineUp(doc) || lined(doc)}
                  title={lineUp(doc) ? undefined : t('lineUpNoRoom')}>{lined(doc) ? t('linedUp') : t('lineUp')}</button>
              </span>
              {shapeDrag && <p className="small ok-text">{t('moveShapeHint')}</p>}
              {!lined(doc) && lineUp(doc) && pair.length === 2 && (
                <p className="muted small">{t('apartBy', { mm: Math.abs(pair[1].y0 + pair[1].got.slide - pair[0].y0 - pair[0].got.slide).toFixed(1) })}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* words on any frame, with all their tools - font, size, colour, shape, turn (2026-10-17) - but a symbol frame's */}
      {!isPlain && !zone.symbolOnly && (
        <div className="pop-block">
          <span className="small strong">{isMessage ? t('messageEdit') : t('noteHere')}</span>
          {note ? (
            <NoteEditor note={note} layout={layouts.get(selected)} hasPicture={!!shown}
              onChange={(n) => setNote(selected, n)} onRemove={() => setNote(selected, null)} />
          ) : (
            <button className="small" onClick={() => setNote(selected, newNote())}>{t('addNote')}</button>
          )}
        </div>
      )}

      {!isPlain && !zone.symbolOnly && (
        <div className="pop-block pop-foot">
          {current && <button className="small" onClick={() => assign(selected, null)}>{t('remove')}</button>}
          <button className="small" title={t('plainMakeHint')} onClick={() => setPlain(selected, true)}>{t('plainMake')}</button>
        </div>
      )}
    </div>
  );

  return (
    <main className="editor">
      <header className="editor-bar">
        <Link href="/" className="button back"
          onClick={(e) => { if (unsaved && !confirm(t('leaveUnsaved'))) e.preventDefault(); else pending.current = autoSave ? pending.current : null; }}>
          ← {t('myCards')}</Link>
        <input className="title-input" defaultValue={project.title} key={project.title} aria-label="Card title"
          onBlur={(e) => rename(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
        <select value={versionId || ''} aria-label="Version" disabled={status === 'saving'}
          onChange={(e) => openVersion(e.target.value)}>
          {versions.map((v) => <option key={v.id} value={v.id}>{v.label} · {ago(v.updatedAt)}</option>)}
        </select>
        <button onClick={() => setNameBar({ mode: 'rename', name: versions.find((v) => v.id === versionId)?.label ?? '' })}
          disabled={status === 'saving'} title={t('renameVersionHint')}>{t('renameVersion')}</button>
        <button onClick={() => setNameBar({ mode: 'copy', name: api.nextVersionLabel(versions.length) })} disabled={status === 'saving'}
          title={unsaved ? t('copyHintUnsaved') : t('copyHint')}>{unsaved ? t('saveAsCopy') : t('duplicateVersion')}</button>
        {versions.length > 1 && (
          <button onClick={removeVersion} disabled={status === 'saving'} title={t('deleteVersionHint')}>{t('deleteVersion')}</button>
        )}
        <button className="primary" onClick={() => setPreviewing(true)}>{t('preview')}</button>
        <button onClick={printCard}>{t('print')}</button>
        <button onClick={foldGuide}>{t('howToFold')}</button>
        {template && envelopeFits(template) && <button onClick={() => void settle().then((go) => go && navigate(`/projects/${project.id}/envelope?v=${versionId}`))}>{t('envMake')}</button>}
        <button onClick={downloadPdf} disabled={out.busy}>{t('downloadPdf')}</button>
        {sharing && <button onClick={sharePdf} disabled={out.busy} title={t('shareHint')}>{t('sharePdf')}</button>}
        <button onClick={saveCard} disabled={out.busy} title={t('saveCardHint')}>{t('saveCard')}</button>
        <ThemeSwitch compact />
        <TipButton />
        <SignOutButton canLeave={() => !unsaved || confirm(t('leaveUnsaved'))} />
        <span className={`save-state ${status}`} aria-live="polite">
          {status === 'saved' ? (folder.status === 'saved' ? t('savedInFolder') : t('saved'))
            : status === 'saving' ? t('saving') : status === 'error' ? t('saveFailed') : status === 'unsaved' ? t('notSaved') : ''}
        </span>
      </header>
      <div className="history-bar" role="toolbar" aria-label={t('editHistory')}>
        <button className="small" onClick={undo} disabled={!past.current.length} title={t('undoHint')}><Icon k="undo" />{t('undo')}</button>
        <button className="small" onClick={redo} disabled={!future.current.length} title={t('redoHint')}><Icon k="redo" />{t('redo')}</button>
        <button className="small" onClick={asOpened} disabled={!opened.current || opened.current.json === JSON.stringify(doc)}
          title={t('asOpenedHint')}>{t('asOpened')}</button>
        <label className="switch small" title={t('autoSaveHint')}>
          <input type="checkbox" checked={autoSave} onChange={(e) => switchAutoSave(e.target.checked)} /> {t('autoSave')}
        </label>
        {!autoSave && (
          <>
            <button className="small primary" onClick={() => void saveNow()} disabled={!unsaved || status === 'saving'}>{t('saveChanges')}</button>
            <button className="small" onClick={cancelChanges} disabled={!unsaved}>{t('cancelChanges')}</button>
          </>
        )}
      </div>
      {nameBar && (
        <form className="banner name-bar" onSubmit={(e) => {
          e.preventDefault();
          void (nameBar.mode === 'copy' ? duplicate(nameBar.name) : renameVersion(nameBar.name)).catch((err) => setOut({ err: (err as Error).message }));
        }}>
          <label>{nameBar.mode === 'copy' ? t('copyName') : t('versionName')}
            <input value={nameBar.name} onChange={(e) => setNameBar({ ...nameBar, name: e.target.value })} maxLength={60} autoFocus required />
          </label>
          <button className="primary">{nameBar.mode === 'copy' ? t('makeCopy') : t('saveName')}</button>
          <button type="button" onClick={() => setNameBar(null)}>{t('cancel')}</button>
          {nameBar.mode === 'copy' && <span className="muted small">{unsaved ? t('copyNoteUnsaved') : t('copyNote')}</span>}
        </form>
      )}
      {oldNote && !past.current.length && !nameBar && (
        <p className="banner old-note" role="status">
          {t('oldCardNote', { when: ago(oldNote) })}{' '}
          <button className="small" onClick={() => setNameBar({ mode: 'copy', name: api.nextVersionLabel(versions.length) })}>{t('workOnCopy')}</button>{' '}
          <button className="small" onClick={() => setOldNote(null)}>{t('close')}</button>
        </p>
      )}
      {(out.msg || out.err) && (
        <p className={`banner out-status ${out.err ? 'error' : 'ok'}`} role="status" onClick={() => !out.busy && setOut({})}>
          {out.err || out.msg}
        </p>
      )}
      {addProblems.length > 0 && (
        <div className="banner out-status error" role="alert" onClick={() => setAddProblems([])}>
          <strong>{t('addProblemsTitle')}</strong>
          {addProblems.map((p) => <div key={p} className="small">{p}</div>)}
        </div>
      )}
      {ready && (
        <p className="banner out-status ok" role="status">
          {t('pdfReady')} <button className="primary" onClick={() => void shareFile(ready)}>{t('sharePdf')}</button>
        </p>
      )}
      {status === 'conflict' && (
        <p className="error banner" role="alert">{t('conflict')} <button onClick={() => location.reload()}>{t('reload')}</button></p>
      )}

      <div className={`editor-body${panelShut ? ' panel-shut' : ''}`}>
        <section className="stage">
          {/* the one place to fill the card - what most people do first (2026-10-17) */}
          <div className="fill-bar">
            <button className="fill-btn" onClick={() => void autoFill(emptyFrames ? 'empty' : 'all')} disabled={!!filling}
              title={t('fillTitle')}>
              <Icon k="sparkle" />{filling ? t('fillWorking') : emptyFrames || !mine.length ? t('fillMyCard') : t('fillAgain')}
            </button>
            {filling && <span className="muted small" role="status">{filling}</span>}
            {!filling && fillNote && <span className="small fill-note" role="status">{fillNote}</span>}
            {undoZones && !filling && <button className="small" onClick={undoFill}><Icon k="undo" />{t('undo')}</button>}
            {!filling && fillNote === t('fillNoPictures') && <UploadButton uploader={uploader} className="small" />}
          </div>
          <div className="stage-bar">
            <nav className="tabs" aria-label="Views">
              {template.views.map((v) => (
                <button key={v.id} aria-pressed={viewId === v.id} title={v.title} onClick={() => setViewId(v.id)}>
                  {hasString(`view_${v.id}`) ? t(`view_${v.id}` as StringKey) : v.title}
                </button>
              ))}
              <button aria-pressed={viewId === 'sheet'} onClick={() => setViewId('sheet')}>{t('viewSheet')}</button>
            </nav>
            <div className="view-tools">
              <label className="switch"><input type="checkbox" checked={showNumbers}
                onChange={(e) => setShowNumbers(e.target.checked)} /> {t('numbers')}</label>
              <button className="lock-toggle" aria-pressed={lockPositions} title={`${lockPositions ? t('lockOn') : t('lockOff')}. ${t('lockPositionsHint')}`}
                onClick={() => { setLockPositions(!lockPositions); setEditing(null); }}>
                <Icon k={lockPositions ? 'lock' : 'unlock'} />{t('lockShort')}
              </button>
            </div>
          </div>
          <div className="view-wrap">
            {view ? (
              <figure className="view">
                <JourneyView {...cardProps} view={view} onSlide={slideHere ? onSlide : undefined} />
                <figcaption className="muted small">{view.title} — {view.note}</figcaption>
              </figure>
            ) : (
              <figure className="view sheet">
                <div className={zoomed ? '' : 'sheet-pair'}>
                  {([1, 2] as const).filter((f) => !zoomed || zone?.printFace === f).map((f) => (
                    <div key={f}><h3 className="small">{t('sheetFace', { n: f })}</h3><SheetFace {...cardProps} face={f} /></div>
                  ))}
                </div>
                <figcaption className="muted small">{t('sheetNote')}</figcaption>
              </figure>
            )}
            {/* zoom, in the card's corner: in on the selected frame, or back to the whole card */}
            <div className="zoom-float">
              {zoomed ? (
                <button onClick={() => setFocus(false)} title={t('zoomWhole')}><Icon k="zoomOut" />{t('zoomWhole')}</button>
              ) : (
                <button onClick={() => setFocus(true)} disabled={!visibleHere} title={t('zoomToZoneHint')}>
                  <Icon k="zoomIn" />{t('zoomToZone', { n: selected })}
                </button>
              )}
            </div>
          </div>
          {!visibleHere && zone?.firstSeen && (
            <p className="hint-bar">{t('notInView', { n: selected })}{' '}
              <button onClick={() => setViewId(zone.firstSeen!)}>{t('showWhere')}</button></p>
          )}
        </section>

        <div className={`side${zone && popup ? ' has-pop' : ''}`}>
          {/* the frame just clicked: its pictures to choose from, and everything about it (2026-10-17) */}
          {zone && popup && (
            <div className={`frame-pop${moving ? ' moving' : ''}`} role="dialog" aria-label={t('zone', { n: selected })}>
              <div className="pop-head">
                <span className="num">{selected}</span>
                <div className="pop-title">
                  <strong>{isPlain ? t('zonePlainShort', { n: selected }) : t('zone', { n: selected })}</strong>
                  <span className="muted small">{where(zone, template)}</span>
                </div>
                <button className="icon-btn" title={zoomed ? t('zoomWhole') : t('zoomFrame')} aria-label={zoomed ? t('zoomWhole') : t('zoomFrame')}
                  onClick={() => zoomTo(selected)}><Icon k={zoomed ? 'zoomOut' : 'zoomIn'} /></button>
                <button className="icon-btn" aria-label={t('prevZone')} title={t('prevZone')}
                  onClick={() => choose(((selected + count - 2) % count) + 1, true)}><Icon k="prev" /></button>
                <button className="icon-btn" aria-label={t('nextZone')} title={t('nextZone')}
                  onClick={() => choose((selected % count) + 1, true)}><Icon k="next" /></button>
                <button className="icon-btn" aria-label={t('close')} title={t('close')} onClick={closePopup}><Icon k="close" /></button>
              </div>
              <div className="pop-body">
                {!isPlain && !zone?.symbolOnly && pickGrid}
                {frameTools}
              </div>
            </div>
          )}

          <div className="pal-wrap">
          {/* the panel folds away to the side with an arrow (2026-10-17; was "Open all / Close all") */}
          <button className="panel-arrow" onClick={() => setPanelShut(!panelShut)} aria-expanded={!panelShut}
            title={panelShut ? t('panelShow') : t('panelHide')} aria-label={panelShut ? t('panelShow') : t('panelHide')}>
            <Icon k={panelShut ? 'prev' : 'next'} />
          </button>
          <aside className={`panel palette${panelShut ? ' shut' : ''}`} aria-label={t('zone', { n: selected })}>
            {panelShut ? (
              <nav className="panel-rail" aria-label={t('panelShow')}>
                {SECTIONS.filter((id) => (id !== 'shape' || (shapeEntry && shapeNow) || zone?.symbolOnly)
                  && !(zone?.symbolOnly && (id === 'picture' || id === 'message'))).map((id) => (
                  <button key={id} className="rail-btn" title={railName(id)} aria-label={railName(id)} onClick={() => showSection(id)}>
                    <SectionIcon id={id} />
                  </button>
                ))}
              </nav>
            ) : (
              <>
                {zone && (
                  <div className="pal-top">
                    <div className="zone-head">
                      <span className={`num big kind-${zone.kind}`}>{zone.number}</span>
                      <div>
                        <h2>{t('zone', { n: zone.number })}</h2>
                        <p className="muted small">{where(zone, template)}</p>
                      </div>
                      <div className="stepper">
                        <button className="icon-btn" aria-label={t('prevZone')} title={t('prevZone')} onClick={() => pick(((selected + count - 2) % count) + 1)}><Icon k="prev" /></button>
                        <button className="icon-btn" aria-label={t('nextZone')} title={t('nextZone')} onClick={() => pick((selected % count) + 1)}><Icon k="next" /></button>
                      </div>
                    </div>
                  </div>
                )}

                {zone && (
                  <>
                    {/* this card's pictures - added, taken from another card, removed - and the library */}
                    {!zone.symbolOnly && (<PaletteSection id="picture" title={t('palPictures')} open={palette.isOpen('picture')} onToggle={() => palette.toggle('picture')}
                      status={isPlain ? t('palPlain') : current && byId.get(current.id)
                        ? <img className="pal-thumb" src={shownThumb(byId.get(current.id)!, doc.zones[selected])} alt=""
                          style={doc.mono ? { filter: 'grayscale(1)' } : undefined} />
                        : t('empty')}>
                      {current && !byId.get(current.id) && (
                        <p className="error small">{t('missingPicture')} <button onClick={() => assign(selected, null)}>{t('remove')}</button></p>
                      )}
                      <div className="row spread pal-subhead first">
                        <span className="small strong">{t('palCardPictures')}</span>
                        <span className="row">
                          <UploadButton uploader={uploader} className="small" />
                          <button className="small" aria-expanded={importing} onClick={() => { setImporting(!importing); setImported(null); }}>{t('importOpen')}</button>
                        </span>
                      </div>
                      {importing && (
                        <div className="import-box">
                          <div className="row spread">
                            <strong className="small">{t('importTitle')}</strong>
                            <button className="icon" aria-label={t('close')} onClick={() => setImporting(false)}>×</button>
                          </div>
                          <p className="muted small">{t('importHint')}</p>
                          {imported && <p className="ok small" role="status">{imported}</p>}
                          {otherCards.length === 0 ? <p className="muted small">{t('importNone')}</p> : otherCards.map(({ card, pics }) => {
                            const fresh = pics.filter((p) => !copiedHere.has(p.id) && !(p.copiedFrom && copiedHere.has(p.copiedFrom)));
                            return (
                              <div key={card.id} className="pal-group">
                                <div className="row spread">
                                  <h5>{card.title}</h5>
                                  {fresh.length > 0 && (
                                    <button className="link" onClick={() => void importPictures(fresh.map((p) => p.id))}>
                                      {t('importAll', { n: fresh.length })}
                                    </button>
                                  )}
                                </div>
                                <ul className="thumbs tray">
                                  {pics.map((p) => {
                                    const here = !fresh.includes(p);
                                    return (
                                      <li key={p.id}>
                                        <button className="thumb-button" disabled={here} aria-pressed={here}
                                          title={here ? t('importHave') : t('importOne')} onClick={() => void importPictures([p.id])}>
                                          <img src={p.urls.thumb} alt={p.name || ''} loading="lazy" />
                                        </button>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {uploader.errors.map((e) => <p key={e} className="error small">{e}</p>)}
                      {pictureNotice && <p className="error small" role="alert">{pictureNotice}</p>}
                      {mine.length === 0 ? <p className="muted small">{t('noPicturesYet')}</p> : (
                        <ul className="thumbs tray">
                          {mine.map((img) => (
                            <li key={img.id}>
                              <button className="thumb-button" aria-pressed={current?.id === img.id}
                                onClick={() => assign(selected, { source: 'upload', id: img.id })}>
                                <img src={img.urls.thumb} alt={img.name || ''} loading="lazy" />
                              </button>
                              {!usedHere.has(img.id) && (
                                <button className="icon remove" title={t('deletePicture')} aria-label={t('deletePicture')}
                                  onClick={() => void removePicture(img)}>×</button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      <h4 className="pal-subhead" id="pal-library">
                        {t('palLibrary')} <span className="muted small">{t('palLibraryCount', { n: library.reduce((n, g) => n + g.items.length, 0) })}</span>
                      </h4>
                      <p className="muted small">{t('pictureLibraryHint')}</p>
                      {library.map((g) => (
                        <div key={g.id} className="pal-group">
                          <h5>{g.name}</h5>
                          <ul className="thumbs tray">
                            {g.items.map((item) => (
                              <li key={item.id}>
                                <button className="thumb-button" title={item.name} disabled={!!libraryBusy}
                                  aria-busy={libraryBusy === item.id} onClick={() => void pickLibrary(item)}>
                                  <img src={item.thumb ?? item.url} alt={item.name} loading="lazy" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </PaletteSection>)}

                    {/* a symbol frame (the square letter's points, 2026-10-31): a heart, a star or a half moon, and its colour */}
                    {symbolPicker && (
                      <PaletteSection id="shape" title={t('palShape')} open={palette.isOpen('shape')} onToggle={() => palette.toggle('shape')}
                        status={symbolNow ? t(`symbol_${symbolNow.shape}` as StringKey) : t('palNone')}>
                        {symbolPicker}
                      </PaletteSection>
                    )}

                    {shapeEntry && shapeNow && (
                      <PaletteSection id="shape" title={t('palShape')} open={palette.isOpen('shape')} onToggle={() => palette.toggle('shape')}
                        status={shapeEntry.pair ? `${t(`shapePair_${shapeNow.kind}` as StringKey)}${lined(doc) ? ` · ${t('linedUp')}` : ''}` : shapeName(shapeNow.kind)}>
                        {shapeEntry.pair ? (
                          <>
                            <p className="muted small">{t('shapePairHint', { a: shapeEntry.pair[0], b: shapeEntry.pair[1] })}</p>
                            <span className="seg" role="group" aria-label={t('palShape')}>
                              {[...new Set(shapeEntry.options.map((o) => o.kind))].map((k) => (
                                <button key={k} aria-pressed={shapeNow.kind === k}
                                  onClick={() => { const o = pairOption(k, shapeNow.sizeName!); if (o) setShape(shapeEntry.pair!, o.key); }}>
                                  {t(`shapePair_${k}` as StringKey)}
                                </button>
                              ))}
                            </span>
                            <h4>{t('shapeSize')}</h4>
                            <span className="seg" role="group" aria-label={t('shapeSize')}>
                              {(['normal', 'big'] as const).map((sz) => {
                                const o = shapeEntry.options.find((x) => x.kind === shapeNow.kind && x.sizeName === sz);
                                return (
                                  <button key={sz} aria-pressed={shapeNow.sizeName === sz} disabled={!o} title={o ? undefined : t('shapeNoRoom')}
                                    onClick={() => o && setShape(shapeEntry.pair!, o.key)}>
                                    {t(`size_${sz}` as StringKey)}
                                  </button>
                                );
                              })}
                            </span>
                            <h4>{t('shapeMeet')}</h4>
                            {/* the two halves side by side, upright as they are in Reveal 1, on ONE scale - where
                                each half's centre is down that view - so handles level are halves lined up; the
                                coloured part of each is where that half may go (2026-10-11) */}
                            {pair && pair.every((p) => p.got.option.slide) && (() => {
                              const top = Math.min(...pair.map((p) => p.y0 + p.got.option.slide!.min));
                              const bottom = Math.max(...pair.map((p) => p.y0 + p.got.option.slide!.max));
                              const pct = (y: number) => ((y - top) / (bottom - top || 1)).toFixed(4);
                              return (
                                <div className="slide-pair">
                                  <span className="slide-ends small muted" aria-hidden="true"><span>▲ {t('slideUp')}</span><span>▼ {t('slideDown')}</span></span>
                                  {pair.map(({ n, got, y0 }) => {
                                    const r = got.option.slide!;
                                    return (
                                      <label key={n} className="slide-col">
                                        <span className="slide-zone">{n}</span>
                                        <span className="slide-track">
                                          <span className="slide-band" style={{ '--a': pct(y0 + r.min), '--b': pct(y0 + r.max) } as CSSProperties} />
                                          <input type="range" min={top} max={bottom} step={0.1} value={y0 + got.slide}
                                            aria-label={t('slideZone', { n })}
                                            aria-valuetext={t('slideAt', { mm: Math.abs(got.slide).toFixed(1), way: got.slide < 0 ? t('slideUp') : t('slideDown') })}
                                            onChange={(e) => setSlide(n, Number(e.target.value) - y0)} />
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            <div className="row">
                              <button onClick={() => { const next = lineUp(doc); if (next) change(next); }} disabled={!lineUp(doc) || lined(doc)}
                                title={lineUp(doc) ? undefined : t('lineUpNoRoom')}>{t('lineUp')}</button>
                              <span className={`small ${lined(doc) ? 'ok' : 'muted'}`}>
                                {lined(doc) ? t('linedUp') : lineUp(doc) ? t('apartBy', { mm: Math.abs(pair![1].y0 + pair![1].got.slide - pair![0].y0 - pair![0].got.slide).toFixed(1) }) : t('lineUpNoRoom')}
                              </span>
                            </div>
                          </>
                        ) : (
                          <span className="seg" role="group" aria-label={t('palShape')}>
                            {shapeEntry.options.map((o) => (
                              <button key={o.key} aria-pressed={shapeNow.key === o.key} onClick={() => setShape([selected], o.key)}>
                                {shapeName(o.kind)}
                              </button>
                            ))}
                          </span>
                        )}
                        <p className="muted small">{t('shapeHint')}</p>
                      </PaletteSection>
                    )}

                    {!zone.symbolOnly && (<PaletteSection id="message" title={t('note')} open={palette.isOpen('message')} onToggle={() => palette.toggle('message')}
                      status={note?.content.trim() ? note.content.trim().split('\n')[0].slice(0, 22) : t('palNone')}>
                      {note ? (
                        <NoteEditor note={note} layout={layouts.get(selected)} hasPicture={!!shown}
                          onChange={(n) => setNote(selected, n)} onRemove={() => setNote(selected, null)} />
                      ) : (
                        <>
                          {zone.defaultContent === 'text' && <p className="note small">{t('messageZone')}</p>}
                          <button onClick={() => setNote(selected, newNote())}>{t('addNote')}</button>
                        </>
                      )}
                    </PaletteSection>)}

                    <PaletteSection id="zones" title={t('allZones')} open={palette.isOpen('zones')} onToggle={() => palette.toggle('zones')}
                      status={t('palFilled', { n: template.zones.filter((z) => filled(z)).length, of: count })}>
                      <ul className="zone-chips">
                        {template.zones.map((z) => {
                          const ref = doc.zones[z.number]?.image;
                          const img = ref && byId.get(ref.id);
                          return (
                            <li key={z.number}>
                              <button aria-pressed={z.number === selected} onClick={() => choose(z.number, true)}
                                className={`chip kind-${z.kind}${doc.zones[z.number]?.plain ? ' plain' : ''}`}
                                style={img && !doc.zones[z.number]?.plain ? { backgroundImage: `url(${shownThumb(img, doc.zones[z.number])})` } : undefined}
                                aria-label={`${t('zone', { n: z.number })}${doc.zones[z.number]?.plain ? `, ${t('palPlain')}` : img ? '' : `, ${t('empty')}`}`}>
                                <span>{z.number}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </PaletteSection>
                  </>
                )}

                {/* invitations with RSVP (2026-10-21): the event, the guests, a card for each */}
                <PaletteSection id="invite" title={t('palInvite')} open={palette.isOpen('invite')} onToggle={() => palette.toggle('invite')}
                  status={doc.invitation ? (ev.data ? ((s) => t(s.guests === 1 ? 'palInviteStatusOne' : 'palInviteStatus', { n: s.guests, yes: s.yes }))(summaryOf(guestsOn(ev.data, project.id))) : '…') : t('palInviteOff')}>
                  <InvitePanel projectId={project.id} invitation={doc.invitation} zone={messageZoneOf(template)} ev={ev}
                    onLinked={linkInvitation} onWrite={writeInvitation} onPrintAll={printInvitations} onDownloadAll={() => void downloadInvitations()}
                    working={inviteWork} />
                </PaletteSection>

                <PaletteSection id="paper" title={t('palPaper')} open={palette.isOpen('paper')} onToggle={() => palette.toggle('paper')}
                  status={<span className="pal-paper" style={{ background: doc.paper?.color ?? '#ffffff', filter: doc.mono ? 'grayscale(1)' : undefined }} title={t(`pattern_${doc.paper?.pattern ?? 'plain'}` as StringKey)} />}>
                  <div className="pal-mono">
                    <button className="mono-switch" role="switch" aria-checked={!!doc.mono} onClick={() => setMono(!doc.mono)}>
                      <span className="track" aria-hidden="true" />{t('monoCard')}
                    </button>
                    <p className="muted small">{t('monoCardHint')}</p>
                  </div>
                  <CardStyle paper={doc.paper} suggestWords={project.title} busy={allBusy} onAllLooks={(l) => void setAllLooks(l)}
                    onPaper={(paper) => {
                      const { paper: _, ...rest } = docRef.current!;
                      change(paper ? { ...rest, paper } : rest);
                    }} />
                </PaletteSection>
              </>
            )}
          </aside>
          </div>
        </div>
      </div>
      {previewing && (
        <PreviewDialog template={template} pictures={pictures} notes={shownNotes} symbols={symbols} invite={inviteCode} paper={doc.paper} mono={doc.mono} plain={plain} startAt={viewId}
          onClose={() => setPreviewing(false)} />
      )}
    </main>
  );
}

/** The name of a section of the tool panel, for its button when the panel is folded away. */
function railName(id: SectionId) {
  return t(({ picture: 'palPictures', shape: 'palShape', message: 'note', zones: 'allZones', invite: 'palInvite', paper: 'palPaper' } as const)[id]);
}
